import { useIncidentImageUpload } from '../hooks/useIncidentImageUpload'
import { useOpenIncident } from '@/hooks/useOpenIncident'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import * as Sentry from '@sentry/nextjs'
import { SubmitHandler, useForm } from 'react-hook-form'

import { Modal, ModalContent, ModalOverlay } from '@chakra-ui/react'
import mixpanel from 'mixpanel-browser'

import { useCreateIncidentMutation } from '@/graphql/generated/hooks'
import { IncidentStatus, IncidentType } from '@/graphql/generated/schemas'

import { getRefetchQueries } from '../utils/refetchQueries'
import { FormInputsIProps } from './../types/types'
import { IncidentModalBody } from './IncidentModalBody'
import { IncidentModalFooter } from './IncidentModalFooter'
import { IncidentModalHeader } from './IncidentModalHeader'

interface CreateIncidentModalIProps {
  isOpen: boolean
  onClose: () => void
}

export const CreateIncidentModal = ({
  isOpen,
  onClose,
}: CreateIncidentModalIProps) => {
  const { shouldEnableCreateIncident } = usePermissions()
  const [createIncident, { loading: isMutationLoading }] =
    useCreateIncidentMutation()
  const { openIncident } = useOpenIncident()

  const form = useForm<FormInputsIProps>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })

  const { formState } = form
  const { isValid } = formState
  const { showSuccess, showError } = useToasts()

  const {
    selectedFiles,
    handleFileSelect,
    removeFile,
    clearFiles,
    uploadImages,
    isUploading: isUploadingImages,
  } = useIncidentImageUpload()

  const isLoading = isMutationLoading || isUploadingImages

  const onSubmit: SubmitHandler<FormInputsIProps> = async (
    values: FormInputsIProps
  ) => {
    if (!values) {
      return
    }

    const address = values.address?.value ? [values.address?.value] : null
    const latitude = values.latitude
      ? Number(values.latitude)
      : values.address?.latitude ?? null
    const longitude = values.longitude
      ? Number(values.longitude)
      : values.address?.longitude ?? null

    const incidentCategoryId: string = values.incidentCategory?.value
    const incidentCategoryName: string = values.incidentCategory?.label

    // DeviceHealth incidents are the only incident category that have specified device,
    // so the type can be inferred based on the value of deviceOrigin form field
    const incidentType =
      values.deviceOrigin === undefined ? null : IncidentType.DeviceHealth

    // If name isn't given, fallback to category
    const nameToUse: string = values.name ? values.name : incidentCategoryName

    try {
      const imageIds = await uploadImages()

      const data = {
        name: nameToUse,
        type: incidentType,
        facilityId: values.facilityOrigin?.value,
        floorId: values.floorOrigin?.value,
        deviceIds: [values.deviceOrigin?.value],
        status: IncidentStatus.Active,
        description: values.description ? values.description : null,
        manualIncidentCategoryId: incidentCategoryId,
        address,
        latitude,
        longitude,
        imageIds: imageIds.length > 0 ? imageIds : undefined,
      }

      const refetchQueries = getRefetchQueries(values.floorOrigin?.value)
      const dataResponse = await createIncident({
        variables: {
          input: data,
        },
        refetchQueries,
      })

      mixpanel.track('Incident Created', {
        incident_id: dataResponse?.data.createIncident.incident.id,
        incident_status: data.status,
        incident_source: values.deviceOrigin?.label,
        device_id: values.deviceOrigin?.value,
        device_type: values.type?.label,
        has_images: imageIds.length > 0,
        image_count: imageIds.length,
      })

      clearFiles()

      onClose()
      showSuccess('Incident created.')
      openIncident({
        incidentId: dataResponse?.data.createIncident.incident.id,
        displayId: dataResponse?.data.createIncident.incident.displayId,
      })
      mixpanel.track('Button Press: Incident Row, Incidents View', {
        incident_id: dataResponse?.data.createIncident.incident.displayId,
        incident_status: data?.status,
        incident_type: data?.type,
      })
    } catch (error) {
      Sentry.captureException(error)
      showError('Failed to create incident. Please try again.')
    }
  }

  const isDirty = form.formState.isDirty

  return (
    <Modal
      closeOnEsc={!isDirty}
      closeOnOverlayClick={!isDirty}
      id='createIncident_modal'
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={onClose}
      size='xl'
    >
      <ModalOverlay />
      <ModalContent bg='#fff'>
        <IncidentModalHeader title='Create Incident' />
        <IncidentModalBody
          form={form}
          onImageRemove={removeFile}
          onImageSelect={handleFileSelect}
          selectedImages={selectedFiles}
        />
        <IncidentModalFooter
          disabled={!isValid || !shouldEnableCreateIncident}
          isLoading={isLoading}
          loadingText={isUploadingImages ? 'Uploading images...' : undefined}
          onClick={form?.handleSubmit(onSubmit)}
          onClose={onClose}
        />
      </ModalContent>
    </Modal>
  )
}
